// angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// 3rd party imports
import { DxPopupComponent } from 'devextreme-angular';
import notify from 'devextreme/ui/notify';

// cc-common imports
import { IconConstants, Utils } from 'cc-common';

// service imports
import { HelperService } from '../../../../_services/shared-services/helper.service';
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { WorkflowEditorService } from '../../../_services/workflow-editor.service';
import { WorkflowVendorEditService } from '../../../_services/workflow-vendor-edit.service';
import { CommunityService } from '../../../../_services/data-services/community.service';
import { WorkflowSharedService } from '../../../../shared-workflow-module/_services/workflow-shared.service';

// model imports
import { WFEBaseParametersDto } from '../../../../shared-workflow-module/_models/wfe-base-parameter-dto';
import { WorkFlowParametersDto } from '../../../../shared-workflow-module/_models/workflow-parameters-dto';
import { WorkflowVendorEditRawDto } from '../../../_models/workflow-vendor-edit/workflow-vendor-edit-raw-dto';
import { WFEVendorResultDto } from '../../../../shared-workflow-module/_models/wfe-vendor-result-dto';
import { WFEOwnerResultDto } from '../../../../shared-workflow-module/_models/wfe-owner-result-dto';
import { WFECommunityResultDto } from '../../../../shared-workflow-module/_models/wfe-community-result-dto';

// constant imports
import { MessageInterval } from '../../../../_constants/messages-intervals';

@Component({
    selector: 'app-workflow-vendor-edit-editor',
    templateUrl: './workflow-vendor-edit-editor.component.html',
    styleUrls: ['./workflow-vendor-edit-editor.component.scss']
})
export class WorkflowVendorEditEditorComponent implements OnInit {
    @ViewChild('workflowVendorEditorPopup', { static: false }) workflowVendorEditorPopup: DxPopupComponent;

    @Input() workflowId: number;
    @Input() communityId: number;

    @Output() onCloseWorkflowVendorEditor = new EventEmitter<boolean>();

    workflowVendorRaw: WorkflowVendorEditRawDto;
    communityGridSource: WFECommunityResultDto[];
    selectedCommunity: WFECommunityResultDto;
    communityGridBoxValue: number[];
    communityColumns: any[];
    communityPayeeColumns: any[];

    payeeVendorBoxGridSource: WFEVendorResultDto[];
    selectedVendorPayee: WFEVendorResultDto;
    payeeVendorGridBoxValue: string[];

    payeeOwnerBoxGridSource: WFEOwnerResultDto[];
    selectedOwnerPayee: WFEOwnerResultDto;
    payeeOwnerGridBoxValue: string[];

    payeeCommunityBoxGridSource: WFECommunityResultDto[];
    selectedCommunityPayee: WFECommunityResultDto;
    payeeCommunityGridBoxValue: number[];

    payeeOptions: any[];
    selectedPayee: string;
    selectedPayeeCode: string;
    vendorColumns: any[];
    ownerPayeeColumns: any[];

    showWorkFlowEditorNotesPopUp = false;
    showWorkFlowEditoryHistoryPopUp = false;

    visible = false;
    isVendorSaved = false;
    loadingVisible = false;

    iconNotes: string = IconConstants.NOTES_AVAILABLE;
    iconEmptyNotes: string = IconConstants.NOTES_EMPTY;
    iconPDF: string = IconConstants.PDF_FILE;
    iconHistory: string = IconConstants.HISTORY;
    iconCancel: string = IconConstants.CANCEL;
    iconCheck: string = IconConstants.CHECK;
    iconRefresh: string = IconConstants.REFRESH;

    constructor(
        private readonly workflowVendorEditService: WorkflowVendorEditService,
        private readonly workflowEditorService: WorkflowEditorService,
        private readonly workflowSharedService: WorkflowSharedService,
        private readonly errorService: ServerErrorService,
        private readonly helperService: HelperService,
        private readonly communityService: CommunityService
    ) {}

    ngOnInit() {
        this.show();
        this.setDefaultPayeeOptions();
        this.getWorkflowVendorToEdit(false);
        this.getCommunityColumns();
        this.getPayeeVendorColumns();
        this.getPayeeOwnerColumns();
    }

    show(): void {
        this.visible = true;
    }

    onEditorShown(): void {
        this.visible = true;
        this.workflowVendorEditorPopup.instance.repaint();
    }

    onEditorClose(): void {
        this.closeVendorEditor();
    }

    onCommunitySelection(selectedObject: any): void {
        this.selectedCommunity = null;
        this.workflowVendorRaw.communityId = null;
        if (!!selectedObject && selectedObject.selectedRow.length > 0) {
            const selectedRows = selectedObject.selectedRow;
            this.selectedCommunity = selectedRows[0];
            this.workflowVendorRaw.communityId = this.selectedCommunity.communityId;
        }
    }

    onVendorPayeeSelection(selectedObject: any): void {
        this.resetPayeeSelection();
        if (!!selectedObject && selectedObject.selectedRow.length > 0) {
            const selectedRows = selectedObject.selectedRow;
            this.selectedVendorPayee = selectedRows[0];
            this.selectedPayeeCode = this.selectedVendorPayee.vendorCode;
        }
    }

    onCommunityPayeeSelection(selectedObject: any): void {
        this.resetPayeeSelection();
        if (!!selectedObject && selectedObject.selectedRow.length > 0) {
            const selectedRows = selectedObject.selectedRow;
            this.selectedCommunityPayee = selectedRows[0];
            this.selectedPayeeCode = this.selectedCommunityPayee.intacctCompanyId;
        }
    }

    onOwnerPayeeSelection(selectedObject: any): void {
        this.resetPayeeSelection();
        if (!!selectedObject && selectedObject.selectedRow.length > 0) {
            const selectedRows = selectedObject.selectedRow;
            this.selectedOwnerPayee = selectedRows[0];
            this.selectedPayeeCode = this.selectedOwnerPayee.customerId;
        }
    }

    onPayeeTypeChanged(event: any): void {
        this.selectedPayee = event.value;
        this.onRefreshClick(false);
    }

    onPdfClick(): void {
        this.downloadInvoice();
    }

    onWorkflowNotesClick(): void {
        this.showWorkFlowEditorNotesPopUp = true;
    }

    onWorkflowNotesPopClose(event): void {
        this.showWorkFlowEditorNotesPopUp = false;
        if (event) {
            this.workflowVendorRaw.hasNotes = true;
            this.workflowVendorEditorPopup.instance.repaint();
        }
    }

    onWorkflowHistoryClick(): void {
        this.showWorkFlowEditoryHistoryPopUp = true;
    }

    onWorkflowHistoryClose(): void {
        this.showWorkFlowEditoryHistoryPopUp = false;
    }

    onSaveAndReturnClick(): void {
        this.resetAllPopups();
        this.saveAndReturnEditedVendor();
    }

    onRefreshClick(forceReload: boolean): void {
        if (this.selectedCommunity) {
            if (Utils.equalsIgnoreCase(this.selectedPayee, 'OWNER')) {
                this.getOwnerData(forceReload);
            }
            if (Utils.equalsIgnoreCase(this.selectedPayee, 'COMMUNITY')) {
                this.getCommunityData(forceReload);
            }
            if (Utils.equalsIgnoreCase(this.selectedPayee, 'VENDOR')) {
                this.getVendorData(forceReload);
            }
        }
    }

    private resetAllPopups(): void {
        this.showWorkFlowEditorNotesPopUp = false;
        this.showWorkFlowEditoryHistoryPopUp = false;
    }

    private resetPayeeSelection(): void {
        this.selectedCommunityPayee = null;
        this.selectedVendorPayee = null;
        this.selectedOwnerPayee = null;
    }

    private setDefaultPayeeOptions(): void {
        this.payeeOptions = [
            {
                value: 'VENDOR',
                displayName: 'Vendor'
            },
            {
                value: 'OWNER',
                displayName: 'Owner'
            },
            {
                value: 'COMMUNITY',
                displayName: 'Community'
            }
        ];

        this.selectedPayee = this.payeeOptions[0].value;
    }

    private getWorkflowVendorToEdit(clearCache: boolean): void {
        const baseParam: WorkFlowParametersDto = new WorkFlowParametersDto();
        baseParam.workflowId = this.workflowId;
        baseParam.communityId = this.communityId;
        baseParam.clearCache = clearCache;

        this.loadingVisible = true;
        this.workflowVendorEditService.getWorkflowVendorEdit(baseParam).subscribe(
            (result: WorkflowVendorEditRawDto) => {
                this.workflowVendorRaw = result;
                this.workflowVendorEditorPopup.instance.repaint();

                this.selectedPayee = this.workflowVendorRaw.payeeType;
                this.selectedPayeeCode = this.workflowVendorRaw.payeeCode;
                this.communityId = this.workflowVendorRaw.communityId;
                // not hiding loading panel as it is immediately shown in getCommunityData
                this.getCommunityData(clearCache);
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private getCommunityData(clearCache: boolean): void {
        const baseParams: WFEBaseParametersDto = new WFEBaseParametersDto();
        baseParams.workflowId = this.workflowVendorRaw.workflowId;
        baseParams.communityId = this.workflowVendorRaw.communityId;
        baseParams.workflowStatus = this.workflowVendorRaw.status;
        baseParams.clearCache = clearCache;

        this.loadingVisible = true;
        this.workflowEditorService.getWorkflowEditorCommunities(baseParams).subscribe(
            (result: WFECommunityResultDto[]) => {
                this.communityGridSource = result;

                this.communityGridSource.forEach((community) => {
                    community['displayText'] = community.displayCommunityText(
                        ['communityName', 'legalName', 'locationName', 'managementCompanyName'],
                        ';'
                    );
                });

                this.communityGridBoxValue = this.workflowVendorRaw.communityId
                    ? [this.workflowVendorRaw.communityId]
                    : [];
                if (Utils.isNullOrUndefined(this.selectedCommunity)) {
                    // not hiding loading panel as it is immediately shown in getVendorData
                    this.getVendorData(clearCache);
                } else {
                    this.payeeCommunityBoxGridSource = this.communityGridSource;
                    this.loadingVisible = false;
                }
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private getVendorData(clearCache: boolean): void {
        const baseParams: WFEBaseParametersDto = new WFEBaseParametersDto();
        baseParams.workflowId = this.workflowVendorRaw.workflowId;
        baseParams.communityId = this.workflowVendorRaw.communityId;
        baseParams.workflowStatus = this.workflowVendorRaw.status;
        baseParams.clearCache = clearCache;

        this.loadingVisible = true;
        this.workflowEditorService.getWorkflowEditorVendors(baseParams).subscribe(
            (result: WFEVendorResultDto[]) => {
                this.payeeVendorBoxGridSource = result;
                this.payeeVendorGridBoxValue = [];
                if (this.workflowVendorRaw.payeeType === 'VENDOR' && !!this.workflowVendorRaw.payeeCode) {
                    this.payeeVendorGridBoxValue = [this.workflowVendorRaw.payeeCode];
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private getOwnerData(clearCache: boolean): void {
        const baseParams: WFEBaseParametersDto = new WFEBaseParametersDto();
        baseParams.workflowId = this.workflowVendorRaw.workflowId;
        baseParams.communityId = this.workflowVendorRaw.communityId;
        baseParams.workflowStatus = this.workflowVendorRaw.status;
        baseParams.clearCache = clearCache;
        this.loadingVisible = true;
        this.workflowEditorService.getWorkflowEditorOwners(baseParams).subscribe(
            (result: WFEOwnerResultDto[]) => {
                this.payeeOwnerBoxGridSource = result;
                this.payeeOwnerGridBoxValue = [];
                if (this.workflowVendorRaw.payeeType === 'OWNER' && !!this.workflowVendorRaw.payeeCode) {
                    this.payeeOwnerGridBoxValue = [this.workflowVendorRaw.payeeCode];
                }
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private saveAndReturnEditedVendor(): void {
        this.workflowVendorRaw.payeeCode = this.selectedPayeeCode;
        this.workflowVendorRaw.payeeType = this.selectedPayee;

        this.loadingVisible = true;
        this.workflowVendorEditService.saveAndReturnVendor(this.workflowVendorRaw).subscribe(
            (result: any) => {
                this.loadingVisible = false;
                const message = `AAP Workflow #${this.workflowVendorRaw.workflowId} updated and returned to status successfully`;
                notify(message, 'Success', MessageInterval.successMsgInterval);
                this.isVendorSaved = true;
                this.closeVendorEditor();
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private downloadInvoice(): void {
        const baseParams = new WorkFlowParametersDto();
        baseParams.workflowId = this.workflowVendorRaw.workflowId;
        baseParams.communityId = this.workflowVendorRaw.communityId;

        this.loadingVisible = true;
        this.workflowSharedService.downloadInvoice(baseParams).subscribe(
            (result: Blob) => {
                const fileURL: string = this.helperService.getFileUrlFromBlob(result);
                window.open(fileURL, '_blank', 'location=yes, scrollbars=yes,status=yes');
                this.loadingVisible = false;
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private closeVendorEditor(): void {
        this.visible = false;
        this.onCloseWorkflowVendorEditor.emit(this.isVendorSaved);
    }

    private getPayeeOwnerColumns(): void {
        this.ownerPayeeColumns = [
            {
                dataField: 'ownerName',
                dataType: 'string',
                caption: 'Name'
            },
            {
                dataField: 'propertyAddress',
                dataType: 'string',
                caption: 'Property Address'
            },
            {
                dataField: 'customerId',
                dataType: 'string',
                caption: 'Customer Id'
            },
            {
                dataField: 'isCurrentOwner',
                dataType: 'boolean',
                caption: 'Current Owner',
                cellTemplate: 'trueValueCellTemplate'
            }
        ];
    }

    private getPayeeVendorColumns(): void {
        this.vendorColumns = [
            {
                dataField: 'vendorName',
                dataType: 'string',
                caption: 'Name'
            },
            {
                dataField: 'remitAddress',
                dataType: 'string',
                caption: 'Address'
            },
            {
                dataField: 'isActive',
                dataType: 'boolean',
                caption: 'Active',
                cellTemplate: 'trueValueCellTemplate'
            }
        ];
    }

    private getCommunityColumns(): void {
        this.communityColumns = this.communityService.getCommunitySelectorColumns();

        this.communityPayeeColumns = [
            {
                dataField: 'locationName',
                dataType: 'string',
                caption: 'Location'
            },
            {
                dataField: 'communityName',
                dataType: 'string',
                caption: 'Community'
            }
        ];
    }
}
